import { createEffect, onCleanup } from "solid-js";

interface UseInfiniteScrollOptions {
  sentinelAccessor: () => HTMLElement | null;
  containerAccessor: () => HTMLElement | null;
  onIntersect: () => void;
  rootMargin?: string;
}

const SCROLL_THRESHOLD_PX = 2000;

export const useInfiniteScroll = (options: UseInfiniteScrollOptions) => {
  createEffect(() => {
    const sentinel = options.sentinelAccessor();
    const container = options.containerAccessor();
    if (!sentinel || !container) {
      return;
    }

    // 1. Intersection Observer (Modern & performant)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          options.onIntersect();
        }
      },
      { root: container, threshold: 0, rootMargin: options.rootMargin ?? "1800px" },
    );
    observer.observe(sentinel);

    // 2. Scroll Listener Fallback (Bulletproof fallback for flexbox layout quirks).
    // Reading scrollHeight/scrollTop/clientHeight forces a synchronous layout,
    // so we coalesce it to once per animation frame — otherwise it reflows on
    // every scroll event and makes a long list feel heavy.
    let rafHandle = 0;
    const handleScroll = () => {
      if (rafHandle) {
        return;
      }
      rafHandle = requestAnimationFrame(() => {
        rafHandle = 0;
        const { scrollHeight, scrollTop, clientHeight } = container;
        if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
          options.onIntersect();
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    onCleanup(() => {
      observer.disconnect();
      container.removeEventListener("scroll", handleScroll);
      if (rafHandle) {
        cancelAnimationFrame(rafHandle);
      }
    });
  });
};
